import { useState, useCallback } from 'react';
import {
  renameMeFavoriteListV1,
  deleteMeFavoriteListV1,
} from '@/services/platform/api/me-—-favorites/me-—-favorites';
import type { FavoriteListDto } from '@/services/platform/api/model';

export interface UseManageFavoriteListResult {
  isLoading: boolean;
  /** Renames a favourite list. Returns the updated list (or null on failure). */
  renameList: (listGuid: string, name: string) => Promise<FavoriteListDto | null>;
  /** Deletes a favourite list. Returns true if the request succeeded. */
  deleteList: (listGuid: string) => Promise<boolean>;
}

export function useManageFavoriteList(): UseManageFavoriteListResult {
  const [isLoading, setIsLoading] = useState(false);

  const renameList = useCallback(async (listGuid: string, name: string): Promise<FavoriteListDto | null> => {
    setIsLoading(true);
    try {
      const response = await renameMeFavoriteListV1(listGuid, { name });
      if (response.status === 200 && response.data) {
        return response.data;
      }
      return null;
    } catch {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteList = useCallback(async (listGuid: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const response = await deleteMeFavoriteListV1(listGuid);
      return response.status === 204;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { isLoading, renameList, deleteList };
}
